import { useAnalysisState } from './useAnalysisState';
import { useAnalysisShortcuts } from './useAnalysisShortcuts';
import { useAnalysisQueries } from './useAnalysisQueries';
import type { CanvasPropsData } from '../types/canvas.types';
import type { SidebarProps } from '../types/sidebar.types';
import { FileMRZ } from '../types/api.types';

export const useAnalysis = () => {
  // States
  const {
    pageIndex,
    setPageIndex,
    toggle,
    handleToggleChange,
    hideDetectionCanvas,
    setHideDetectionCanvas,
    renderFontsPannel,
    setRenderFontsPannel,
    hoverDetection,
    setHoverDetection,
    isPopupOpen,
    setIsPopupOpen,
    isPointerInSidebar,
    setIsPointerInSidebar,
    selected2DDocId,
    selectedMrzId,
    isNotesModalOpen,
    setIsNotesModalOpen,
  } = useAnalysisState();

  // Shortcuts
  useAnalysisShortcuts({
    isPopupOpen,
    setIsPopupOpen,
    handleToggleChange,
  });

  // Queries
  const {
    resultData,
    isResultLoading,
    isImageLoading,
    imageData,
    downloadData,
    notesData,
  } = useAnalysisQueries(pageIndex, toggle);

  const selected2DDoc = selected2DDocId
    ? resultData?.detections.find(
        (detection) => detection.id === selected2DDocId
      )?.['2ddoc']
    : undefined;
  const selectedMrz: FileMRZ = selectedMrzId
    ? (resultData?.detections.find(
        (detection) => detection.id === selectedMrzId
      )?.mrz_chars as FileMRZ)
    : [];

  // Sidebar props
  const sidebarProps: SidebarProps = {
    pageIndex,
    setPageIndex,
    resultData: resultData,
    notesData,
    toggle,
    handleToggleChange,
    renderFontsPannel: null,
    setHoverDetection,
    hoverDetection,
    hideDetectionCanvas,
    setIsPointerInSidebar,
    selected2DDoc,
    selectedMrz,
    selectedMrzId,
    isResultLoading,
  };

  // Canvas props
  const canvasProps: CanvasPropsData = {
    resultData,
    imageData,
    downloadData,
    pageIndex,
    setPageIndex,
    toggle,
    handleToggleChange,
    setIsPopupOpen,
    hoverDetection,
    setHoverDetection,
    hideDetectionCanvas,
    setHideDetectionCanvas,
    selected2DDocId,
    selectedMrzId,
    selectedMrz,
    notesData,
    setIsNotesModalOpen,
    setRenderFontsPannel,
    isPointerInSidebar,
    isImageLoading,
    isResultLoading,
  };

  return {
    // States
    pageIndex,
    setPageIndex,
    toggle,
    handleToggleChange,
    hideDetectionCanvas,
    setHideDetectionCanvas,
    renderFontsPannel,
    setRenderFontsPannel,
    hoverDetection,
    setHoverDetection,
    isPopupOpen,
    setIsPopupOpen,
    isPointerInSidebar,
    setIsPointerInSidebar,
    isNotesModalOpen,
    setIsNotesModalOpen,

    // Data
    resultData,
    isResultLoading,
    isImageLoading,
    imageData,
    downloadData,
    notesData,
    selected2DDoc,
    selectedMrz,
    selected2DDocId,
    selectedMrzId,

    // Props
    canvasProps,
    sidebarProps,
  };
};
